import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { create } from 'zustand';
import type { FriendStatus, MediaType, PresenceInput, PresencePlace } from '@teaplus/shared';
import { api } from './api';
import { useCanSocialise } from './social';

/** The title page open right now, for "Looking at Interstellar". */
const useLooking = create<{
  title: { mediaId: string; contentType: MediaType; title: string } | null;
}>(() => ({ title: null }));

/** The title page open right now, if any. */
export const lookingAt = () => useLooking.getState().title;

export const useLookingTitle = () => useLooking((s) => s.title);

/** Called by the film and series pages while they're open. */
export function useLookingAt(t: { id: string; type: MediaType; title: string } | null | undefined) {
  const key = t ? `${t.type}:${t.id}:${t.title}` : '';
  useEffect(() => {
    if (!t) return;
    useLooking.setState({ title: { mediaId: t.id, contentType: t.type, title: t.title } });
    return () => useLooking.setState({ title: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}

const PLACES: [RegExp, PresencePlace][] = [
  [/^\/movies/, 'movies'],
  [/^\/tv/, 'tv'],
  [/^\/discover/, 'discover'],
  [/^\/search/, 'search'],
  [/^\/(library|collection|history)/, 'library'],
  [/^\/friends/, 'friends'],
  [/^\/party/, 'party'],
  [/^\/downloads/, 'downloads'],
  [/^\/addons/, 'addons'],
  [/^\/settings/, 'settings'],
];
const placeOf = (pathname: string): PresencePlace =>
  PLACES.find(([re]) => re.test(pathname))?.[1] ?? 'home';

const REPORT_EVERY_MS = 30_000;
/** Fired when the live notifications connection (re)opens. */
export const LIVE_CONNECTED = 'tp:live-connected';

const send = (p: PresenceInput, keepalive = false) =>
  void api('/api/presence', { body: p, keepalive }).catch(() => undefined);

/**
 * Tells friends what you're doing in Tea+ (on their Friends page, never as a notification):
 * browsing a section, looking at a title, or away when the window is hidden.
 */
export function useReportPresence() {
  const can = useCanSocialise();
  const { pathname } = useLocation();
  const looking = useLooking((s) => s.title);
  const hidden = useRef(typeof document !== 'undefined' && document.visibilityState === 'hidden');
  const report = useRef<() => void>(() => undefined);

  report.current = () => {
    const status: FriendStatus = hidden.current
      ? { state: 'away' }
      : looking && /^\/(movie|series)\//.test(pathname)
        ? { state: 'looking', ...looking }
        : { state: 'browsing', place: placeOf(pathname) };
    send(status);
  };

  // As it changes (a moment after, so clicking through pages doesn't send each one).
  useEffect(() => {
    if (!can) return;
    const t = window.setTimeout(() => report.current(), 400);
    return () => window.clearTimeout(t);
  }, [can, pathname, looking]);

  useEffect(() => {
    if (!can) return;
    const beat = window.setInterval(() => report.current(), REPORT_EVERY_MS);
    const onVis = () => {
      hidden.current = document.visibilityState === 'hidden';
      report.current();
    };
    const bye = () => send({ state: 'offline' }, true);
    const again = () => report.current();
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('pagehide', bye);
    window.addEventListener(LIVE_CONNECTED, again);
    return () => {
      window.removeEventListener(LIVE_CONNECTED, again);
      window.clearInterval(beat);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('pagehide', bye);
    };
  }, [can]);
}

const PLACE_LABELS: Record<PresencePlace, string> = {
  home: 'Browsing Home',
  movies: 'Browsing Movies',
  tv: 'Browsing TV Shows',
  discover: 'Discovering something new',
  search: 'Searching',
  library: 'Browsing My Library',
  friends: 'Hanging out in Friends',
  party: 'In a Party',
  downloads: 'Looking at Downloads',
  addons: 'Browsing Addons',
  settings: 'In Settings',
};

/** "Browsing Movies" for the section at this address. */
export const placeText = (pathname: string) => PLACE_LABELS[placeOf(pathname)];

/** "Browsing Movies", "Looking at Interstellar", "Away". */
export const statusText = (s: FriendStatus) =>
  s.state === 'browsing'
    ? PLACE_LABELS[s.place]
    : s.state === 'looking'
      ? `Looking at ${s.title}`
      : 'Away';
